import { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@/components/data-grid/core/DataGrid';
import { KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import {
  useGetClubRegistrationsQuery,
  useApproveClubRegistrationMutation,
  useRejectClubRegistrationMutation,
} from '@/services/ClubRegistrationService';
import { useGetAvailableHarborsQuery } from '@/services/ClubRegistrationService';
import { IClubRegistrationData } from '@/types/club-registration.types';
import { PaginationState } from '@tanstack/react-table';
import { RegistrationViewModal } from './RegistrationViewModal.tsx';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';
import { usePermissions } from '@/hooks/usePermissions';

type Row = IClubRegistrationData & {
  yachtClubAdmin?: {
    id: string;
    name: string;
    email: string;
    profile_picture: string | null;
  } | null;
};

export const RegistrationListings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { resetSearch } = usePagination();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    mode: 'view' | 'approve';
    id?: string;
  }>({ isOpen: false, mode: 'view' });

  const [selectedHarborIds, setSelectedHarborIds] = useState<string[]>([]);

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    id: string | null;
    action: 'approve' | 'reject' | null;
  }>({ isOpen: false, id: null, action: null });

  const { hasPermission } = usePermissions();

  const { data, isLoading, isFetching, refetch } = useGetClubRegistrationsQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm.trim() || undefined,
    },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  const [approveRegistration] = useApproveClubRegistrationMutation();
  const [rejectRegistration] = useRejectClubRegistrationMutation();

  const selectedRegistrationRegionId =
    (modalState.id &&
      data?.data?.find((r) => r.id === modalState.id)?.region?.id) ||
    '';

  const { data: availableHarbors, isLoading: isHarborsLoading } =
    useGetAvailableHarborsQuery(selectedRegistrationRegionId, {
      skip: !(
        modalState.isOpen &&
        modalState.mode === 'approve' &&
        modalState.id &&
        hasPermission('read_harbors_for_region') &&
        !!selectedRegistrationRegionId
      ),
    });

  const submitApproval = async (harborIds: string[]) => {
    if (!modalState.id) return;
    try {
      const call = approveRegistration({
        id: modalState.id,
        data: { harborIds },
      });
      if (call && typeof call.unwrap === 'function') {
        await call.unwrap();
      } else {
        await call;
      }
      enqueueSnackbar('Registration approved', {
        variant: 'success',
      });
      setModalState({ isOpen: false, mode: 'view' });
      setSelectedHarborIds([]);
      refetch();
    } catch (e: unknown) {
      const error = e as { data?: { message?: string } };
      enqueueSnackbar(error?.data?.message || 'Failed to approve', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchTerm, pagination.pageIndex, pagination.pageSize, refetch]);

  const rows: Row[] = useMemo(() => data?.data || [], [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const resetFilters = () => {
    if (searchTerm) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
    resetSearch();
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const onConfirm = async () => {
    if (!confirmState.id || !confirmState.action) return;
    try {
      if (confirmState.action === 'approve') {
        // Approve now handled via modal
        return;
      } else {
        const call = rejectRegistration(confirmState.id);
        if (call && typeof call.unwrap === 'function') {
          await call.unwrap();
        } else {
          await call;
        }
        enqueueSnackbar('Registration rejected', { variant: 'success' });
      }
      setConfirmState({ isOpen: false, id: null, action: null });
      refetch();
    } catch (e: unknown) {
      const error = e as { data?: { message?: string } };
      enqueueSnackbar(error?.data?.message || 'Action failed', {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        header: 'Location',
        accessorKey: 'location',
        cell: ({ row }: { row: { original: Row } }) =>
          row.original.location || '-',
      },
      {
        header: 'Contact Email',
        accessorKey: 'contact_email',
      },
      {
        header: 'Website',
        accessorKey: 'website',
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: Row } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('read_club_registration_requests') && (
              <button
                data-testid={`view-${row.original.id}`}
                onClick={() =>
                  setModalState({
                    isOpen: true,
                    mode: 'view',
                    id: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" />
              </button>
            )}
            {hasPermission('approve_reject_club_registration') && (
              <>
                <button
                  data-testid={`approve-${row.original.id}`}
                  onClick={() => {
                    setSelectedHarborIds([]);
                    setModalState({
                      isOpen: true,
                      mode: 'approve',
                      id: row.original.id || '',
                    });
                  }}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Approve ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="check" />
                </button>
                <button
                  data-testid={`reject-${row.original.id}`}
                  onClick={() =>
                    setConfirmState({
                      isOpen: true,
                      id: row.original.id || '',
                      action: 'reject',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Reject ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              </>
            )}
          </div>
        ),
        meta: { className: 'w-[160px]' },
      },
    ],
    [hasPermission]
  );

  return (
    <div className="space-y-4">
      <div className="card card-grid min-w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-registration"
              aria-label="Search registrations"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-registration"
                placeholder="Search registrations"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search registrations by name"
              />
            </label>
          </div>
          {searchTerm !== '' && (
            <div className="flex items-center">
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="p-4">
          {isLoading || isFetching ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: data?.totalItems || 0,
                totalPages: data?.totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'users', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={searchTerm !== ''}
            />
          )}
        </div>
      </div>

      <Modal
        open={modalState.isOpen}
        onClose={() => {
          setModalState({ isOpen: false, mode: 'view' });
          setSelectedHarborIds([]);
        }}
        className="!flex items-center justify-center"
        title={modalState.mode === 'approve' ? '' : 'View Registration'}
      >
        {modalState.mode === 'view' ? (
          modalState.id ? (
            <RegistrationViewModal
              id={modalState.id}
              onClose={() => setModalState({ isOpen: false, mode: 'view' })}
            />
          ) : (
            <div />
          )
        ) : (
          <div
            className="bg-gray-100 p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar rounded-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-harbors-title"
            aria-label="Assign Harbors"
            aria-describedby="assign-harbors-description"
            tabIndex={-1}
          >
            <h1
              id="assign-harbors-title"
              className="text-lg font-medium text-gray-900 mb-4"
            >
              Assign Harbors
            </h1>
            {isHarborsLoading ? (
              <div className="py-8 text-center text-sm text-gray-600">
                Loading harbors...
              </div>
            ) : (
              <>
                {availableHarbors && availableHarbors.harbors.length > 0 ? (
                  <>
                    <div
                      id="assign-harbors-description"
                      className="text-sm text-gray-700 mb-4"
                    >
                      Harbour assignment is optional. Select harbors to assign,
                      or approve with none:
                    </div>
                    <div className="max-h-64 overflow-auto border border-gray-200 rounded-md p-3 mb-6 custom-scrollbar">
                      <div className="space-y-2">
                        {availableHarbors.harbors.map((h) => (
                          <label
                            key={h.id}
                            className="flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-gray-200 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedHarborIds.includes(h.id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedHarborIds((prev) =>
                                  checked
                                    ? [...prev, h.id]
                                    : prev.filter((x) => x !== h.id)
                                );
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                            />
                            <span className="text-sm text-gray-900 select-none cursor-pointer">
                              {h.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    id="assign-harbors-description"
                    className="text-sm text-gray-700 mb-6 text-center py-4"
                  >
                    No harbors available to assign to this registration. You can
                    still approve the Yacht Club with no harbour association.
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      setModalState({ isOpen: false, mode: 'view' });
                      setSelectedHarborIds([]);
                    }}
                    aria-label="Cancel harbor assignment"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => submitApproval(selectedHarborIds)}
                  >
                    {selectedHarborIds.length > 0
                      ? `Approve (${selectedHarborIds.length} harbor${selectedHarborIds.length > 1 ? 's' : ''})`
                      : 'Approve'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <ConfirmationDialog
        open={confirmState.isOpen}
        onClose={() =>
          setConfirmState({ isOpen: false, id: null, action: null })
        }
        onConfirm={onConfirm}
        message={
          confirmState.action === 'approve'
            ? 'Are you sure you want to approve this registration?'
            : 'Are you sure you want to reject this registration?'
        }
        confirmButtonText={
          confirmState.action === 'approve' ? 'Approve' : 'Reject'
        }
      />
    </div>
  );
};
